import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { LinearProgress, Grid, Typography } from '@mui/material'
import AdvisorApi from '../../../../api/AdvisorApi'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FormConvertibleEntity } from '../../types/FormConvertibleEntity'
import { AdvisorListItem } from '@finance-lab/utils/types'
import { AdvisorList } from './AdminAdvisorList'
import { AdvisorWithEventualId, CategorizedAdvisor } from './types'
import { AddAdvisorFields } from './AdminAddAdvisorFields'

const advisorApi = new AdvisorApi()

function AdvisorDetails({
    entityType,
    setIsCustomFormDirty,
}: {
    entityType: 'issue' | 'convertible'
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const formMethods = useFormContext<FormIssueEntity | FormConvertibleEntity>()
    const entityAdvisors = formMethods.watch('advisors')
    const [allAdvisors, setAllAdvisors] = useState<Array<AdvisorListItem>>([])

    const categorizedAdvisors = useMemo(() => {
        const categorized = entityAdvisors.reduce(
            (acc, advisor) => {
                if (advisor.type === 'financial_advisor') {
                    acc.financialAdvisors.push(advisor)
                } else if (advisor.type === 'legal_advisor') {
                    acc.legalAdvisors.push(advisor)
                } else if (advisor.type === 'issuing_agent' && entityType === 'issue') {
                    acc.issuingAgents.push(advisor)
                } else {
                    throw new Error(`Unknown advisor type: ${advisor.type}`)
                }
                return acc
            },
            { financialAdvisors: [], legalAdvisors: [], issuingAgents: [] } as Record<
                'financialAdvisors' | 'legalAdvisors' | 'issuingAgents',
                Array<AdvisorWithEventualId>
            >,
        )
        return categorized
    }, [entityType, entityAdvisors])

    useEffect(() => {
        async function fetchData() {
            const advs = await advisorApi.findAll()

            setAllAdvisors(advs.sort((a, b) => a.name.localeCompare(b.name)))
        }
        void fetchData()
    }, [])

    if (!entityAdvisors) return <LinearProgress />

    const handleAdd = (newAdvisor: CategorizedAdvisor) => {
        // if the same advisor is already present (name, type combination), do not add it again
        if (entityAdvisors.find((a) => a.name === newAdvisor.name && a.type === newAdvisor.type)) return
        const newAdvisors: AdvisorWithEventualId[] = [...entityAdvisors, newAdvisor]
        setIsCustomFormDirty(true)
        formMethods.setValue('advisors', newAdvisors)
    }
    const onDeleteAdvisor = (advisor: AdvisorWithEventualId) => {
        const newAdvisors = entityAdvisors.filter((a) => {
            if (advisor.advisorId) return !(a.advisorId === advisor.advisorId && a.type === advisor.type)
            else return !(a.name === advisor.name && a.type === advisor.type)
        })
        setIsCustomFormDirty(true)
        formMethods.setValue('advisors', newAdvisors)
    }
    const gridSize = entityType === 'issue' ? { xs: 12, md: 4 } : { xs: 12, md: 6 }

    return (
        <Grid container spacing={2}>
            <Grid size={gridSize}>
                <Typography variant='subtitle1'>Finansiella rådgivare</Typography>
                <AdvisorList advisors={categorizedAdvisors.financialAdvisors} onDelete={onDeleteAdvisor} />
                <AddAdvisorFields
                    advisors={allAdvisors}
                    entityType={entityType}
                    selectedAdvisors={categorizedAdvisors.financialAdvisors}
                    onSelect={handleAdd}
                    advisorType='financial_advisor'
                    setIsCustomFormDirty={setIsCustomFormDirty}
                />
            </Grid>
            <Grid size={gridSize}>
                <Typography variant='subtitle1'>Legala rådgivare</Typography>
                <AdvisorList advisors={categorizedAdvisors.legalAdvisors} onDelete={onDeleteAdvisor} />
                <AddAdvisorFields
                    advisors={allAdvisors}
                    entityType={entityType}
                    selectedAdvisors={categorizedAdvisors.legalAdvisors}
                    onSelect={handleAdd}
                    advisorType='legal_advisor'
                    setIsCustomFormDirty={setIsCustomFormDirty}
                />
            </Grid>
            {entityType === 'issue' ? (
                <Grid size={gridSize}>
                    <Typography variant='subtitle1'>Emissionsinstitut</Typography>
                    <AdvisorList advisors={categorizedAdvisors.issuingAgents} onDelete={onDeleteAdvisor} />
                    <AddAdvisorFields
                        advisors={allAdvisors}
                        entityType={entityType}
                        selectedAdvisors={categorizedAdvisors.issuingAgents}
                        onSelect={handleAdd}
                        advisorType='issuing_agent'
                        setIsCustomFormDirty={setIsCustomFormDirty}
                    />
                </Grid>
            ) : null}
        </Grid>
    )
}

export default AdvisorDetails
